<template>
  <div class="pt-40">
    <div class="scheduler-heading">
      Select time period this survey should appear
      <span class="study_duration">Study ends on day {{ studyDuration }}</span>
    </div>
    <div class="pb-15" v-for="(item, index) in value.duration" :key="index">
      <div class="row fs-12 color_959fa4">
        <div class="col-md-2 pl-0">Start</div>
        <div class="col-md-6 pl-0">End</div>
      </div>
      <div class="row">
        <div class="col-md-1 pl-0 pr-0">
          <span class="day_label">Day</span>
        </div>
        <div class="col-md-1 pl-0">
          <input type="number" v-validate="'required|min_value:1'" :name="'startDay' + index" class="form-control" :value="item.startDay" @input="durationDataChanged(index, 'startDay', Number($event.target.value))" />
        </div>
        <div class="col-md-1 pl-0 pr-0">
          <span class="day_label">Day</span>
        </div>
        <div class="col-md-1 pl-0">
          <input type="number" class="form-control" v-validate="'required|min_value:1'" :name="'endDay' + index" :disabled="value.allowUntillDuration && index + 1 === value.duration.length" :value="item.endDay" @input="durationDataChanged(index, 'endDay', Number($event.target.value))" />
        </div>
        <div class="col-md-2 pl-0 color_blue cursor_pointer pt-5" name="addAnotherDuration" @click="addAnotherDuration(index)" v-if="value.duration.length === index + 1 && !value.allowUntillDuration">Add another time period</div>
        <div class="col-md-2 pl-0 color_red cursor_pointer pt-5" :name="'removeDuration' + index" @click="removeDuration(index)" v-if="value.duration.length !== index + 1">Remove</div>
      </div>
    </div>
    <div class="pt-10">
      <label class="fw-normal">
        <input type="checkbox" class="checkbox_position" name="allowUntillDuration" :checked="value.allowUntillDuration" @input="allowUntilDurationChanged($event.target.checked)" />
        <span class=""> Available until the end of participant's study duration</span>
      </label>
    </div>
  </div>
</template>

<script>
/**
 * This is a generic component to handle time period input for scheduling an activity.
 * It should be used when there is a custom time period schedule
 */
export default {
  name: "scheduler-time-period",
  props: {
    value: {
      type: Object,
      required: true
    },
    studyDuration: {
      type: Number,
      required: true
    },
    studyData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      timePeriod: {
        allowUntillDuration: false,
        duration: [
          {
            startDay: null,
            endDay: null
          }
        ]
      }
    };
  },
  methods: {
    durationDataChanged(index, property, value) {
      this.timePeriod.duration[index][property] = value;
      this.$emit("input", this.timePeriod);
    },
    addAnotherDuration() {
      this.$set(this.timePeriod.duration, this.timePeriod.duration.length, { startDay: null, endDay: null });
      this.$emit("input", this.timePeriod);
    },
    removeDuration(index) {
      this.$delete(this.timePeriod.duration, index);
      this.$emit("input", this.timePeriod);
    },
    allowUntilDurationChanged(value) {
      this.timePeriod.allowUntillDuration = value;
      if (this.timePeriod.allowUntillDuration) {
        this.timePeriod.duration[this.timePeriod.duration.length - 1].endDay = null;
      }
      this.$emit("input", this.timePeriod);
    },
    // this validations where based on the method 'checkValidation' from the component 'scheduler.vue'
    validateTimePeriod() {
      let timePeriodErrorMessage = null;
      let isTimePeriodValid = true;

      for (let index = 0; index < this.value.duration.length; index++) {
        const period = this.value.duration[index];

        const isLast = this.value.duration.length - 1 === index;
        const nextStartDay = isLast ? null : this.value.duration[index + 1].startDay;

        const isStartDayValid = this.validateNumberGreaterThanZeroNoDecimal(period.startDay);
        const isEndDayValid = this.validateNumberGreaterThanZeroNoDecimal(period.endDay);

        // all days should be valid except for the last endDay wich should be null if allowUntillDuration is true
        if (!isStartDayValid || (!isEndDayValid && (!isLast || !this.value.allowUntillDuration))) {
          timePeriodErrorMessage = "Start and End days should be greater than 0 and cannot have decimals.";
          isTimePeriodValid = false;
          break;
        }

        if (this.studyData.screeningMilestoneAllowed && this.value.duration[0].startDay < -this.studyData.screeningDuration) {
          timePeriodErrorMessage = "Start Day cannot be less than screening Duration";
          isTimePeriodValid = false;
          break;
        }

        // all days should be sorted except for the last endDay wich should be null if allowUntillDuration is true
        if ((period.startDay >= period.endDay && (!isLast || !this.value.allowUntillDuration)) || (!isLast && nextStartDay && period.endDay > nextStartDay)) {
          timePeriodErrorMessage = "Start and end days should be in increasing order.";
          isTimePeriodValid = false;
          break;
        }

        if (!isLast && nextStartDay && period.endDay === nextStartDay) {
          timePeriodErrorMessage = "Scheduled start and end days overlap. Please revise.";
          isTimePeriodValid = false;
          break;
        }

        if (period.startDay > this.studyDuration || period.endDay > this.studyDuration) {
          timePeriodErrorMessage = "Start and end day should be less than study duration.";
          isTimePeriodValid = false;
          break;
        }
      }

      return {
        timePeriodErrorMessage,
        isTimePeriodValid
      };
    },
    validateNumberGreaterThanZeroNoDecimal(number) {
      return number && number > 0 && number % 1 === 0;
    }
  },
  created() {
    this.timePeriod = { ...this.value };
  }
};
</script>

<style lang="scss" scoped>
.scheduler-heading {
  font-weight: 600;
  color: #666769;
}
.study_duration {
  display: inline-block;
  color: #4c8ce4;
  background-color: #edf3fc;
  padding: 2px 5px;
  border-radius: 2px;
}
.day_label {
  display: block;
  width: 100%;
  height: 34px;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 1.42857143;
  color: #888;
  background-color: #fafafa;
  background-image: none;
  border: 1px solid #ccc;
}
</style>
